import styled, { css } from 'styled-components';
import { Icon } from 'src/components';

export const Screen = styled.div(
  ({ theme }) => css`
    position: fixed;
    inset: 0;
    z-index: ${theme.zi.z1};
    background-color: ${theme.co.tertiary0};
    overflow: hidden;
  `,
);

export const Panel = styled.div(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${theme.sz.s20} ${theme.sz.s4} ${theme.sz.s8};
    overflow-y: auto;

    @media (max-width: ${theme.sz.md}) {
      padding: ${theme.sz.s16} ${theme.sz.s4} ${theme.sz.s6};
      align-items: flex-start;
    }
  `,
);

// Leaves room at the top for the speaker avatar to overlap the edge.
export const Card = styled.div(
  ({ theme }) => css`
    position: relative;
    width: min(100%, ${theme.sz.ms});
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s4};
    margin-top: ${theme.sz.s12};
    padding: ${theme.sz.s16} ${theme.sz.s8} ${theme.sz.s8};
    border-radius: ${theme.bo.large};
    background-color: ${theme.co.secondary99};
    box-shadow: 0 ${theme.sz.s2} ${theme.sz.s8} rgb(0 17 34 / 30%);
    text-align: center;

    @media (max-width: ${theme.sz.md}) {
      padding: ${theme.sz.s14} ${theme.sz.s4} ${theme.sz.s6};
    }
  `,
);

export const Avatar = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    left: 50%;
    width: ${theme.sz.s24};
    height: ${theme.sz.s24};
    transform: translate(-50%, -50%);
    border: ${theme.bo.thickWidth} solid ${theme.co.neutral99};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.tertiary60};
    overflow: hidden;
  `,
);

export const AvatarVideo = styled.video(
  () => css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 25%;
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    margin: ${theme.sz.s0};
    color: ${theme.co.secondary0};
    ${theme.typography.heading.desktop.l};

    @media (max-width: ${theme.sz.md}) {
      ${theme.typography.heading.mobile.m};
    }
  `,
);

export const Description = styled.p(
  ({ theme }) => css`
    max-width: ${theme.sz.xs};
    margin: ${theme.sz.s0};
    color: ${theme.co.secondary40};
    ${theme.typography.paragraph.base};
  `,
);

export const PractisedPill = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s2};
    padding: ${theme.sz.s2} ${theme.sz.s5};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary20};
    color: ${theme.co.secondary0};
    ${theme.typography.heading.desktop.xxs};

    svg {
      font-size: ${theme.sz.s4};
    }
  `,
);

export const WordList = styled.ul(
  ({ theme }) => css`
    width: 100%;
    margin: ${theme.sz.s2} ${theme.sz.s0};
    padding: ${theme.sz.s0};
    list-style: none;
  `,
);

export const WordRow = styled.li(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: ${theme.sz.s6} 1fr 1fr;
    align-items: center;
    gap: ${theme.sz.s2};
    padding: ${theme.sz.s3} ${theme.sz.s0};
    border-bottom: ${theme.bo.thinWidth} solid ${theme.co.secondary95};
    text-align: left;
  `,
);

export const WordTick = styled(Icon)<{ $isComplete: boolean }>(
  ({ theme, $isComplete }) => css`
    color: ${$isComplete ? theme.co.success40 : theme.co.secondary80};
    font-size: ${theme.sz.s5};
  `,
);

export const WordTarget = styled.span(
  ({ theme }) => css`
    color: ${theme.co.secondary0};
    ${theme.typography.heading.desktop.xxs};
  `,
);

export const WordSource = styled.span(
  ({ theme }) => css`
    color: ${theme.co.secondary50};
    ${theme.typography.paragraph.m};
  `,
);

export const Actions = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    margin-top: ${theme.sz.s2};
  `,
);

export const SecondaryActions = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s3};

    > * {
      flex: 1;
    }

    @media (max-width: ${theme.sz.md}) {
      flex-direction: column;
    }
  `,
);
